import {
  HEALTH_RESOLUTIONS,
  healthSeriesResponse,
  type HealthResolution,
  type HealthSample,
  type HealthSeriesPoint,
  type HealthSeriesResponse,
} from '@occ/contracts';
import { get } from '../api/http';
import { ROUTES } from '../api/routes';

/**
 * Serie histórica de salud (Módulo 7).
 *
 * El backend **sí** la sirve: `node_health_samples` persiste los latidos a
 * resolución fina y los agrega a tramos de cinco minutos y de una hora, cada nivel
 * con su retención. `GET /nodes/:id/health` elige el nivel según el rango pedido.
 *
 * La serie que el panel acumula del canal en vivo no desaparece por eso: sigue
 * siendo el **tramo más reciente**. Entre que el nodo late y que la fila está
 * escrita hay un volcado en lote de unos segundos, así que lo último que se ha
 * visto por el WebSocket es más nuevo que lo último que la base tiene. Sin
 * empalmarlas, la gráfica de un nodo que se está mirando ahora mismo terminaría
 * unos segundos en el pasado y parecería congelada.
 */

export type HealthSeries = HealthSample[];

/** Muestras que el panel guarda por nodo. A un latido cada 30 s son ~2 horas. */
export const LIVE_SERIES_LIMIT = 240;

/**
 * Añade una muestra a la serie en memoria, descartando la más vieja al llenarse.
 *
 * Se salta la muestra si su marca de tiempo no es posterior a la última: el
 * canal en vivo puede reenviar la misma tras una reconexión, y duplicarla
 * dibujaría un escalón que no ocurrió.
 */
export const appendSample = (
  series: HealthSeries | undefined,
  sample: HealthSample,
): HealthSeries => {
  const current = series ?? [];
  const last = current.at(-1);
  if (last && last.sampledAt.getTime() >= sample.sampledAt.getTime()) return current;
  const next = [...current, sample];
  return next.length > LIVE_SERIES_LIMIT ? next.slice(next.length - LIVE_SERIES_LIMIT) : next;
};

/**
 * Pide la serie histórica.
 *
 * `resolution` se deja sin mandar a propósito en el uso normal: el backend la
 * elige por el rango y puede degradarla si el nivel fino ya venció, y forzarla
 * desde aquí convertiría «los últimos 30 días» en una respuesta vacía.
 */
export const fetchHealthSeries = async (
  nodeId: string,
  sinceMinutes: number,
  resolution?: HealthResolution,
): Promise<HealthSeriesResponse> => {
  const to = new Date();
  const from = new Date(to.getTime() - sinceMinutes * 60_000);

  return get(ROUTES.nodeHealthSeries(nodeId), healthSeriesResponse, {
    from: from.toISOString(),
    to: to.toISOString(),
    resolution,
  });
};

/**
 * Empalma el tramo en vivo al final del histórico.
 *
 * Solo lo estrictamente más nuevo que el último punto persistido. Con `>=` en vez
 * de `>` se dibujaría dos veces el mismo instante, y en una gráfica eso es un
 * escalón que no ocurrió.
 *
 * No se empalma nada cuando el nivel servido es grueso: pegar puntos de 30 s al
 * final de una serie de tramos horarios haría que el último tramo pareciera
 * treinta veces más ancho de lo que es, y la curva daría un salto en el borde que
 * se lee como un incidente.
 */
export const mergeLiveTail = (
  persisted: HealthSeriesPoint[],
  live: HealthSample[] | undefined,
  resolution: HealthResolution,
): HealthSample[] => {
  if (!live || live.length === 0) return persisted;
  if (resolution !== 'raw') return persisted;

  const last = persisted.at(-1);
  if (!last) return live;

  const cola = live.filter((sample) => sample.sampledAt.getTime() > last.sampledAt.getTime());
  return cola.length === 0 ? persisted : [...persisted, ...cola];
};

/** Etiqueta del grano servido, para que la pantalla pueda decir qué se está viendo. */
export const resolutionLabel = (resolution: HealthResolution): string =>
  HEALTH_RESOLUTIONS[resolution].label;
